import { ErrorHandler, ErrorType } from '@/components/error-handler';
import { useBaleOutboundContext } from './hook';

/**
 * Error handler specifically for the bale outbound workflow.
 * Maps workflow-specific errors to user-friendly messages and displays them using the ErrorHandler component.
 */
export const BaleOutboundErrorHandler = () => {
	const { error, setError } = useBaleOutboundContext();

	// Map workflow errors to ErrorHandler format
	const getErrorConfig = (error: string): ErrorType | null => {
		switch (error) {
			case 'NOT_AUTHENTICATED':
				return {
					type: 'error',
					style: 'full',
					title: 'Authentication Required',
					details: 'Please log in to continue with the bale outbound process.',
				};

			case 'NOT_AUTHORISED':
				return {
					type: 'error',
					style: 'full',
					title: 'Access Denied',
					details: 'You do not have permission to perform bale outbound operations.',
				};

			case 'NOT_AUTHORISED_FOR_FACILITY':
				return {
					type: 'error',
					style: 'full',
					title: 'Facility Access Denied',
					details: 'You are not authorized to access this facility.',
				};

			case 'FACILITY_NOT_AUTHORISED_FOR_BALE_OUTBOUND':
				return {
					type: 'error',
					style: 'full',
					title: 'Feature Not Available',
					details: 'This facility is not authorized for bale outbound operations.',
				};

			case 'INVALID_CODE':
				return {
					type: 'error',
					style: 'minimal',
					title: 'Invalid Code',
					details: 'The scanned code is not valid or has already been processed.',
					duration: 6000,
				};

			case 'NO_BALES_FOUND':
				return {
					type: 'warning',
					style: 'minimal',
					title: 'No Bales Found',
					details: 'No bales were found matching the scanned code.',
					duration: 4000,
				};

			case 'BALE_ALREADY_DISPATCHED':
				return {
					type: 'warning',
					style: 'minimal',
					title: 'Bale Already Dispatched',
					details: 'This bale has already been dispatched and cannot be processed again.',
					duration: 5000,
				};

			case 'CODE_NOT_FOUND':
				return {
					type: 'warning',
					style: 'minimal',
					title: 'Code Not Found',
					details: 'The scanned code was not found in the system.',
					duration: 4000,
				};

			case 'OVER_WEIGHT_LIMIT':
				return {
					type: 'warning',
					style: 'minimal',
					title: 'Weight Limit Exceeded',
					details: 'The entered weight exceeds the maximum allowed limit for this load.',
					duration: 5000,
				};

			case 'NETWORK_ERROR':
				return {
					type: 'error',
					style: 'full',
					title: 'Connection Error',
					details:
						'Unable to connect to the server. Please check your internet connection and try again.',
				};

			case 'CREATION_FAILED':
				return {
					type: 'error',
					style: 'full',
					title: 'Operation Failed',
					details: 'Failed to create the outbound bale record. Please try again.',
				};

			case 'PHOTO_UPLOAD_FAILED':
				return {
					type: 'error',
					style: 'full',
					title: 'Photo Upload Failed',
					details:
						'One or more photos failed to upload. Please check your connection and try again.',
				};

			default:
				return null;
		}
	};

	const errorConfig = error ? getErrorConfig(error) : null;

	return <ErrorHandler error={errorConfig} setError={() => setError(null)} />;
};
